import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import {
  fetchAvailableDates,
  submitAppointmentRequest,
} from '../api/appointments'
import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import {
  fetchSpecialist,
  specialistKey,
  type ClinicSession,
  type Specialist,
} from '../api/specialists'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { AmtcFallback, DatePicker } from '../components/specialists/DatePicker'
import { SessionPicker } from '../components/specialists/SessionPicker'
import { ApiError } from '../lib/api'
import { formatPesewas } from '../lib/money'
import { setPatientSession } from '../lib/patientSession'

/**
 * Request an appointment with a specialist at one hospital (M14-T08, ADR-0029).
 * A REQUEST, not a booking: no payment step, no card/MoMo field. The patient picks
 * a real clinic session and a date the server offers (weekday + two-week window),
 * verifies their phone (OTP), and submits — then sees "expect a call back from
 * AMTC". A hospital with no sessions shows the AMTC fallback instead of the form.
 */
export function AppointmentRequest(): ReactNode {
  const { specialistSlug = '', hospitalSlug = '' } = useParams()
  const navigate = useNavigate()

  const specialistQuery = useQuery({
    queryKey: specialistKey(specialistSlug),
    queryFn: ({ signal }) => fetchSpecialist(specialistSlug, signal),
    retry: (count, err) =>
      err instanceof ApiError && err.code === 'not_found' ? false : count < 2,
  })

  return (
    <PageShell measure={594}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">
          Request an appointment
        </span>
      </header>
      {/* One band for the body; nothing nested restates the measure and no
          padding moves, so mobile is unchanged by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {specialistQuery.isPending ? (
          <p
            className="px-4 py-12 text-center text-[13px] text-ink-3"
            role="status"
          >
            Loading…
          </p>
        ) : specialistQuery.isError ? (
          <p
            className="px-4 py-12 text-center text-[13px] text-ink-3"
            role="alert"
          >
            Couldn&apos;t load this specialist.
          </p>
        ) : (
          <Form
            specialistSlug={specialistSlug}
            hospitalSlug={hospitalSlug}
            specialist={specialistQuery.data}
            onSent={(reference) =>
              navigate('/appointments/sent', {
                state: {
                  reference,
                  specialistName: specialistQuery.data.full_name,
                },
              })
            }
          />
        )}
      </PageBand>
    </PageShell>
  )
}

function Form({
  specialistSlug,
  hospitalSlug,
  specialist,
  onSent,
}: {
  specialistSlug: string
  hospitalSlug: string
  specialist: Specialist
  onSent: (reference: string) => void
}): ReactNode {
  const hospital = specialist.hospitals.find((h) => h.slug === hospitalSlug)
  const sessions = hospital?.sessions ?? []

  const [session, setSession] = useState<ClinicSession | null>(null)
  const [date, setDate] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [medications, setMedications] = useState('')
  const [insurance, setInsurance] = useState('')
  const [area, setArea] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [authSession, setAuthSession] = useState<PatientSession | null>(null)

  const datesQuery = useQuery({
    queryKey: ['available-dates', specialistSlug, hospitalSlug, session?.id],
    queryFn: ({ signal }) =>
      fetchAvailableDates(specialistSlug, hospitalSlug, session!.id, signal),
    enabled: session !== null,
  })

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    // Cache so "View my requests" opens without a second OTP; a cold visit
    // still re-verifies.
    onSuccess: (s) => {
      setAuthSession(s)
      setPatientSession(s)
    },
  })
  const submitMutation = useMutation({
    mutationFn: () => {
      if (authSession === null || session === null || date === null)
        throw new Error('not ready')
      return submitAppointmentRequest(authSession.token, {
        specialistSlug,
        hospitalSlug,
        clinicSessionId: session.id,
        requestedDate: date,
        reasonForVisit: reason.trim(),
        ...(medications.trim()
          ? { currentMedications: medications.trim() }
          : {}),
        ...(insurance.trim() ? { insuranceProvider: insurance.trim() } : {}),
        ...(area.trim() ? { area: area.trim() } : {}),
      })
    },
    onSuccess: (res) => onSent(res.reference),
  })

  // A hospital with no clinic sessions can't be requested here — hand to AMTC.
  if (sessions.length === 0) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 py-5">
        <Summary specialist={specialist} hospitalName={hospital?.name} />
        <AmtcFallback />
      </div>
    )
  }

  const ready =
    session !== null &&
    date !== null &&
    reason.trim().length >= 3 &&
    authSession !== null

  return (
    <div className="flex flex-1 flex-col gap-5 px-4 py-5">
      <Summary specialist={specialist} hospitalName={hospital?.name} />

      <SessionPicker
        sessions={sessions}
        selectedId={session?.id ?? null}
        onSelect={(s) => {
          setSession(s)
          setDate(null)
        }}
      />

      {session !== null &&
        (datesQuery.isPending ? (
          <p className="text-[13px] text-ink-3" role="status">
            Loading dates…
          </p>
        ) : datesQuery.isError ? (
          <p className="text-[13px] text-ink-3" role="alert">
            Couldn&apos;t load dates. Try again.
          </p>
        ) : (
          <DatePicker
            dates={datesQuery.data}
            selected={date}
            onSelect={setDate}
          />
        ))}

      <Field label="Reason for visit" required>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          aria-label="Reason for visit"
          placeholder="e.g. chest pain, routine cardiac check…"
          className="w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </Field>
      <Field label="Current medications" optional>
        <input
          type="text"
          value={medications}
          onChange={(e) => setMedications(e.target.value)}
          aria-label="Current medications"
          placeholder='List medications or write "None"'
          className="w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </Field>
      <Field label="Insurance provider" optional>
        <input
          type="text"
          value={insurance}
          onChange={(e) => setInsurance(e.target.value)}
          aria-label="Insurance provider"
          placeholder="e.g. NHIS, Enterprise, None"
          className="w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </Field>
      <Field label="Your area" optional>
        <input
          type="text"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          aria-label="Your area"
          placeholder="e.g. East Legon, Accra"
          className="w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </Field>

      <div className="flex flex-col gap-2">
        <span className="text-[13px] font-medium text-ink-1">
          Verify your phone
        </span>
        {authSession ? (
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-bed-good-bg px-2.5 py-1 text-[13px] font-medium text-bed-good">
            <Icon name="check" size={14} /> Verified · {phone.trim()}
          </p>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="tel"
                aria-label="Phone number"
                placeholder="Phone, e.g. +233…"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              />
              <button
                type="button"
                disabled={phone.trim().length < 8 || requestMutation.isPending}
                onClick={() => requestMutation.mutate()}
                className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {requestMutation.isPending
                  ? 'Sending…'
                  : codeSent
                    ? 'Resend'
                    : 'Send code'}
              </button>
            </div>
            {requestMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(requestMutation.error)}
              </p>
            )}
            {codeSent && (
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Verification code"
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <button
                  type="button"
                  disabled={code.trim().length < 4 || verifyMutation.isPending}
                  onClick={() => verifyMutation.mutate()}
                  className="shrink-0 rounded-r3 bg-accent px-4 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Verify
                </button>
              </div>
            )}
            {verifyMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(verifyMutation.error)}
              </p>
            )}
          </>
        )}
      </div>

      {specialist.consult_fee_pesewas != null && (
        <p className="text-[12px] text-ink-3">
          Consultation:{' '}
          <span className="tabular-nums">
            {formatPesewas(specialist.consult_fee_pesewas)}
          </span>{' '}
          — AMTC will confirm your appointment; no payment now.
        </p>
      )}

      <button
        type="button"
        disabled={!ready || submitMutation.isPending}
        onClick={() => submitMutation.mutate()}
        className="rounded-r3 bg-accent px-4 py-2.5 text-sm font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {submitMutation.isPending ? 'Sending…' : 'Send request'}
      </button>
      {submitMutation.isError && (
        <p role="alert" className="text-[12px] text-bed-bad">
          {errMsg(submitMutation.error)}
        </p>
      )}
    </div>
  )
}

function Summary({
  specialist,
  hospitalName,
}: {
  specialist: {
    full_name: string
    specialty: string
    consult_fee_pesewas: number | null
  }
  hospitalName: string | undefined
}): ReactNode {
  return (
    <div className="rounded-r3 border border-accent bg-accent-bg p-3">
      <div className="text-[14px] font-semibold text-ink-1">
        {specialist.full_name}
      </div>
      <div className="text-[12px] text-ink-2">{specialist.specialty}</div>
      {hospitalName && (
        <div className="mt-1 text-[12px] text-ink-2">{hospitalName}</div>
      )}
      {specialist.consult_fee_pesewas != null && (
        <div className="mt-1 text-[12px] text-ink-2">
          Consultation{' '}
          <span className="tabular-nums font-medium">
            {formatPesewas(specialist.consult_fee_pesewas)}
          </span>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  required,
  optional,
  children,
}: {
  label: string
  required?: boolean
  optional?: boolean
  children: ReactNode
}): ReactNode {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium text-ink-1">
        {label}
        {required && <span className="text-bed-bad"> *</span>}
        {optional && (
          <span className="font-normal text-ink-3"> (optional)</span>
        )}
      </span>
      {children}
    </label>
  )
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'rate_limited')
      return 'Too many requests. Please try again later.'
    if (err.code === 'date_outside_window')
      return 'That date is too far out — call the AMTC team to arrange it.'
    return err.message
  }
  return 'Something went wrong. Please try again.'
}
